<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Helpdesk / Support</h1>
      </div>

      <b-tabs content-class="mt-3" nav-class="mb-0">
        <!-- Search & Support Tab -->
        <b-tab title="Support" active>
          <!-- Search Bar -->
          <div class="app-card shadow-sm mb-4">
            <div class="app-card-body p-4">
              <div class="row">
                <div class="col-md-8">
                  <b-input-group>
                    <b-form-input
                      v-model="searchQuery"
                      placeholder="Search by email, name, phone, or matric number..."
                      @keyup.enter="searchHelpdesk"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button variant="primary" @click="searchHelpdesk" :disabled="searching || !searchQuery.trim()">
                        <b-spinner small v-if="searching"></b-spinner>
                        <b-icon v-else icon="search"></b-icon> Search
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
              </div>
            </div>
          </div>

          <!-- No results -->
          <div v-if="searched && !applicants.length && !students.length" class="text-center text-muted py-4">
            No applicants or students found for "{{ searchQuery }}".
          </div>

          <!-- Applicant Results -->
          <div v-if="applicants.length" class="app-card shadow-sm mb-4">
            <div class="app-card-header p-3 border-bottom">
              <h5 class="mb-0">Applicants ({{ applicants.length }})</h5>
            </div>
            <div class="app-card-body p-4">
              <div v-for="applicant in applicants" :key="applicant.id" class="border rounded p-3 mb-3">
                <div class="row">
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Name:</strong> {{ [applicant.surname, applicant.firstname, applicant.lastname].filter(Boolean).join(' ') }}</p>
                    <p class="mb-1"><strong>Email:</strong> {{ applicant.email }}</p>
                    <p class="mb-1"><strong>Phone:</strong> {{ applicant.mobile || 'N/A' }}</p>
                  </div>
                  <div class="col-md-6 text-md-right">
                    <button class="btn btn-sm btn-outline-primary mr-1 mb-1" @click="openEditContactModal(applicant)">
                      <b-icon icon="pencil"></b-icon> Update Contact
                    </button>
                    <button class="btn btn-sm btn-outline-warning mr-1 mb-1" @click="resetApplicantPassword(applicant)">
                      <b-icon icon="key"></b-icon> Reset Password
                    </button>
                    <button
                      v-if="applicant.has_successful_caution || applicant.has_successful_acceptance"
                      class="btn btn-sm btn-success mr-1 mb-1"
                      @click="createStudentRecord(applicant)"
                      :disabled="creatingStudent === applicant.id"
                    >
                      <b-spinner small v-if="creatingStudent === applicant.id"></b-spinner>
                      <b-icon v-else icon="person-plus"></b-icon>
                      {{ applicant.has_student_record ? 'Add New Programme' : 'Create Student Record' }}
                    </button>
                  </div>
                </div>

                <!-- Applications -->
                <div v-if="applicant.applications && applicant.applications.length" class="mt-3">
                  <h6 class="text-muted">Applications:</h6>
                  <b-table
                    :items="applicant.applications"
                    :fields="applicationFields"
                    small
                    bordered
                    responsive
                  >
                    <template #cell(status)="data">
                      <b-badge :variant="statusVariant(data.value)">{{ data.value }}</b-badge>
                    </template>
                    <template #cell(actions)="data">
                      <div class="btn-group btn-group-sm">
                        <button
                          v-if="data.item.status === 'approved' && data.item.admissionLetter"
                          class="btn btn-info"
                          @click="viewAdmissionLetter(data.item)"
                          title="View Admission Letter"
                        >
                          <b-icon icon="file-earmark-text"></b-icon>
                        </button>
                        <button
                          v-if="data.item.status === 'approved' && data.item.admissionLetter"
                          class="btn btn-success"
                          @click="resendAdmissionEmail(data.item)"
                          :disabled="resending === data.item.id"
                          title="Resend Admission Email"
                        >
                          <b-spinner small v-if="resending === data.item.id"></b-spinner>
                          <b-icon v-else icon="envelope"></b-icon>
                        </button>
                      </div>
                    </template>
                  </b-table>
                </div>

                <!-- Applicant Transactions -->
                <div v-if="applicant.transactions && applicant.transactions.length" class="mt-3">
                  <h6 class="text-muted">Payment Transactions:</h6>
                  <b-table
                    :items="applicant.transactions"
                    :fields="applicantTxnFields"
                    small
                    bordered
                    responsive
                  >
                    <template #cell(status)="data">
                      <b-badge :variant="data.value === 'SUCCESS' ? 'success' : data.value === 'PENDING' ? 'warning' : 'secondary'">{{ data.value || 'PENDING' }}</b-badge>
                    </template>
                    <template #cell(amount)="data">
                      {{ data.value ? Number(data.value).toLocaleString() : '' }}
                    </template>
                    <template #cell(reference)="data">
                      {{ data.item.rrr || data.item.gateway_ref || 'N/A' }}
                    </template>
                    <template #cell(txn_actions)="data">
                      <b-button
                        v-if="data.item.status !== 'SUCCESS'"
                        size="sm"
                        variant="outline-primary"
                        @click="requeryRow('applicant', data.item)"
                        :disabled="requerying === (data.item.rrr || data.item.gateway_ref)"
                        title="Requery Gateway"
                      >
                        <b-spinner small v-if="requerying === (data.item.rrr || data.item.gateway_ref)"></b-spinner>
                        <b-icon v-else icon="arrow-repeat"></b-icon>
                      </b-button>
                      <b-badge v-else variant="success">Paid</b-badge>
                    </template>
                  </b-table>
                </div>
              </div>
            </div>
          </div>

          <!-- Student Results -->
          <div v-if="students.length" class="app-card shadow-sm mb-4">
            <div class="app-card-header p-3 border-bottom">
              <h5 class="mb-0">Students ({{ students.length }})</h5>
            </div>
            <div class="app-card-body p-4">
              <div v-for="student in students" :key="student.id" class="border rounded p-3 mb-3">
                <div class="row">
                  <div class="col-md-6">
                    <p class="mb-1"><strong>Name:</strong> {{ [student.surname, student.first_name, student.last_name].filter(Boolean).join(' ') }}</p>
                    <p class="mb-1"><strong>Email:</strong> {{ student.email }}</p>
                    <p class="mb-1"><strong>Phone:</strong> {{ student.phone || 'N/A' }}</p>
                    <p class="mb-1"><strong>Matric No:</strong> {{ student.matric_collect || student.matric_no || 'N/A' }}</p>
                  </div>
                  <div class="col-md-6 text-md-right">
                    <button class="btn btn-sm btn-outline-warning mr-1 mb-1" @click="resetStudentPassword(student)">
                      <b-icon icon="key"></b-icon> Reset Password
                    </button>
                  </div>
                </div>

                <!-- Degrees -->
                <div v-if="student.degrees && student.degrees.length" class="mt-3">
                  <h6 class="text-muted">Degrees:</h6>
                  <b-table
                    :items="student.degrees"
                    :fields="degreeFields"
                    small
                    bordered
                    responsive
                  >
                    <template #cell(status)="data">
                      <b-badge :variant="data.value === 'CURRENT' ? 'success' : 'secondary'">{{ data.value }}</b-badge>
                    </template>
                    <template #cell(degree_actions)="data">
                      <b-button size="sm" variant="outline-info" @click="checkDegreeFees(student, data.item)" title="Check Fee Match" class="mr-1">
                        <b-spinner small v-if="checkingFees === data.item.id"></b-spinner>
                        <b-icon v-else icon="cash-stack"></b-icon>
                      </b-button>
                      <b-button size="sm" variant="outline-primary" @click="openEditDegreeModal(student, data.item)" title="Edit Degree">
                        <b-icon icon="pencil"></b-icon>
                      </b-button>
                    </template>
                  </b-table>
                </div>

                <!-- Student Transactions -->
                <div v-if="student.transactions && student.transactions.length" class="mt-3">
                  <h6 class="text-muted">Payment Transactions:</h6>
                  <b-table
                    :items="student.transactions"
                    :fields="studentTxnFields"
                    small
                    bordered
                    responsive
                  >
                    <template #cell(status)="data">
                      <b-badge :variant="data.value === 'SUCCESS' ? 'success' : data.value === 'PENDING' ? 'warning' : 'secondary'">{{ data.value || 'PENDING' }}</b-badge>
                    </template>
                    <template #cell(amount)="data">
                      {{ data.value ? Number(data.value).toLocaleString() : '' }}
                    </template>
                    <template #cell(reference)="data">
                      {{ data.item.rrr || data.item.gateway_ref || 'N/A' }}
                    </template>
                    <template #cell(txn_actions)="data">
                      <b-button
                        v-if="data.item.status !== 'SUCCESS'"
                        size="sm"
                        variant="outline-primary"
                        @click="requeryRow('student', data.item)"
                        :disabled="requerying === (data.item.rrr || data.item.gateway_ref)"
                        title="Requery Gateway"
                      >
                        <b-spinner small v-if="requerying === (data.item.rrr || data.item.gateway_ref)"></b-spinner>
                        <b-icon v-else icon="arrow-repeat"></b-icon>
                      </b-button>
                      <b-badge v-else variant="success">Paid</b-badge>
                    </template>
                  </b-table>
                </div>

                <!-- Course Registrations with Courses -->
                <div v-if="student.registrations && student.registrations.length" class="mt-3">
                  <h6 class="text-muted">Course Registrations:</h6>
                  <div v-for="reg in student.registrations" :key="reg.id" class="border rounded p-2 mb-2">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <span>
                        <strong>{{ reg.session_name }}</strong> - {{ reg.semester_name }}
                        <span v-if="reg.level" class="ml-2 text-muted">(Level {{ reg.level }})</span>
                      </span>
                      <b-badge :variant="reg.reg_status === 'approved' ? 'success' : reg.reg_status === 'disapproved' ? 'danger' : 'warning'">{{ reg.reg_status || 'pending' }}</b-badge>
                    </div>
                    <b-table
                      v-if="reg.courses && reg.courses.length"
                      :items="reg.courses"
                      :fields="courseFields"
                      small
                      bordered
                      responsive
                      class="mb-0"
                    >
                      <template #cell(course_status)="data">
                        <b-badge :variant="data.value === 'C' ? 'primary' : 'secondary'">{{ data.value === 'C' ? 'Compulsory' : data.value === 'E' ? 'Elective' : data.value || 'N/A' }}</b-badge>
                      </template>
                    </b-table>
                    <small v-else class="text-muted">No courses registered</small>
                  </div>
                </div>

                <!-- Semester Calendar -->
                <div v-if="student.semesters && student.semesters.length" class="mt-3">
                  <h6 class="text-muted">Registered Semesters:</h6>
                  <b-table
                    :items="student.semesters"
                    :fields="semesterFields"
                    small
                    bordered
                    responsive
                  >
                  </b-table>
                </div>

                <!-- ID Card Exemptions -->
                <div v-if="student.idcard_exemptions && student.idcard_exemptions.length" class="mt-3">
                  <h6 class="text-muted">ID Card Exemptions:</h6>
                  <b-table
                    :items="student.idcard_exemptions"
                    :fields="idcardFields"
                    small
                    bordered
                    responsive
                  >
                  </b-table>
                </div>
              </div>
            </div>
          </div>

        </b-tab>

        <!-- Audit Log Tab -->
        <b-tab title="Audit Log" @click="loadAuditLogs">
          <div class="app-card shadow-sm mb-4">
            <div class="app-card-body p-4">
              <div class="row mb-3">
                <div class="col-md-3">
                  <b-form-select v-model="auditFilter.action" @change="loadAuditLogs">
                    <option value="">All Actions</option>
                    <option value="update_applicant_contact">Update Contact</option>
                    <option value="reset_applicant_password">Reset Applicant Password</option>
                    <option value="reset_student_password">Reset Student Password</option>
                    <option value="resend_admission_email">Resend Admission Email</option>
                    <option value="requery_payment">Requery Payment</option>
                    <option value="update_student_degree">Update Student Degree</option>
                    <option value="approve_access_request">Approve Access</option>
                  </b-form-select>
                </div>
                <div class="col-md-3">
                  <b-form-input type="date" v-model="auditFilter.date_from" placeholder="From date" @change="loadAuditLogs"></b-form-input>
                </div>
                <div class="col-md-3">
                  <b-form-input type="date" v-model="auditFilter.date_to" placeholder="To date" @change="loadAuditLogs"></b-form-input>
                </div>
                <div class="col-md-3 text-right">
                  <button class="btn btn-outline-primary btn-sm" @click="loadAuditLogs" :disabled="loadingLogs">
                    <b-spinner small v-if="loadingLogs"></b-spinner>
                    <b-icon v-else icon="arrow-clockwise"></b-icon> Refresh
                  </button>
                </div>
              </div>

              <div v-if="loadingLogs" class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading audit logs...</div>
              </div>

              <b-table
                v-else
                :items="auditLogs"
                :fields="auditFields"
                :per-page="auditPerPage"
                :current-page="auditPage"
                responsive
                striped
                hover
                show-empty
              >
                <template #empty>
                  <div class="text-center text-muted py-4">No audit logs found.</div>
                </template>
                <template #cell(action)="data">
                  <b-badge variant="dark">{{ formatAction(data.value) }}</b-badge>
                </template>
                <template #cell(details)="data">
                  <small>{{ formatDetails(data.value) }}</small>
                </template>
                <template #cell(created_at)="data">
                  {{ formatDateTime(data.value) }}
                </template>
              </b-table>

              <div class="d-flex justify-content-between align-items-center" v-if="auditLogs.length > auditPerPage">
                <small class="text-muted">{{ auditLogs.length }} log entries</small>
                <b-pagination
                  v-model="auditPage"
                  :total-rows="auditLogs.length"
                  :per-page="auditPerPage"
                  size="sm"
                ></b-pagination>
              </div>
            </div>
          </div>
        </b-tab>
      </b-tabs>

      <!-- Edit Contact Modal -->
      <b-modal
        id="edit-contact-modal"
        title="Update Applicant Contact"
        @ok="updateApplicantContact"
        ok-title="Update"
        :ok-disabled="updatingContact"
      >
        <div v-if="editContact.applicant">
          <div class="mb-3">
            <strong>{{ [editContact.applicant.surname, editContact.applicant.firstname].filter(Boolean).join(' ') }}</strong>
          </div>
          <b-form-group label="Email">
            <b-form-input v-model="editContact.email" type="email"></b-form-input>
          </b-form-group>
          <b-form-group label="Phone">
            <b-form-input v-model="editContact.phone"></b-form-input>
          </b-form-group>
          <div v-if="updatingContact" class="text-center mt-2">
            <b-spinner small variant="primary"></b-spinner> Updating...
          </div>
        </div>
      </b-modal>

      <!-- Admission Letter Modal -->
      <b-modal
        id="admission-letter-modal"
        title="Admission Letter"
        size="lg"
        ok-only
        ok-title="Close"
        scrollable
      >
        <div v-if="loadingLetter" class="text-center my-4">
          <b-spinner variant="primary"></b-spinner>
        </div>
        <div v-else-if="admissionLetterHtml" v-html="admissionLetterHtml"></div>
        <div v-else class="text-center text-muted py-4">No admission letter found.</div>
      </b-modal>

      <!-- Edit Degree Modal -->
      <b-modal
        id="edit-degree-modal"
        title="Edit Student Degree"
        @ok="saveStudentDegree"
        ok-title="Save"
        :ok-disabled="savingDegree"
      >
        <b-form-group label="Programme" description="Search by name, code, or degree">
          <v-select
            v-model="editDegree.prog_id"
            :options="programmes"
            :filter="filterProgrammes"
            :get-option-label="o => o.prog_code + ' - ' + o.programme"
            :reduce="o => o.id"
            placeholder="Select programme..."
            @input="onProgrammeSelect"
          ></v-select>
        </b-form-group>
        <b-form-group label="Degree">
          <b-form-input v-model="editDegree.degree" placeholder="e.g. PGD, MSC, PHD"></b-form-input>
        </b-form-group>
        <b-form-group label="Programme Code">
          <b-form-input v-model="editDegree.prog_code" placeholder="e.g. TPM*PGD*FT"></b-form-input>
        </b-form-group>
        <b-form-group label="Grant Type">
          <b-form-select v-model="editDegree.grant_type">
            <option value="">-- Select --</option>
            <option value="RUN">RUN</option>
            <option value="OTHERS">OTHERS</option>
          </b-form-select>
        </b-form-group>
        <b-form-group label="Admission Session">
          <b-form-input v-model="editDegree.admission_session" placeholder="e.g. 2023/2024"></b-form-input>
        </b-form-group>
        <b-form-group label="Status">
          <b-form-select v-model="editDegree.status">
            <option value="CURRENT">CURRENT</option>
            <option value="GRADUATED">GRADUATED</option>
            <option value="SUSPENDED">SUSPENDED</option>
            <option value="WITHDRAWN">WITHDRAWN</option>
          </b-form-select>
        </b-form-group>
        <div v-if="savingDegree" class="text-center mt-2">
          <b-spinner small variant="primary"></b-spinner> Saving...
        </div>
      </b-modal>

      <!-- Fee Check Modal -->
      <b-modal
        id="fee-check-modal"
        :title="'Fee Schedule Check — ' + (feeCheck.prog_code || '')"
        size="lg"
        ok-only
        ok-title="Close"
        scrollable
      >
        <div v-if="feeCheck.loading" class="text-center my-4">
          <b-spinner variant="primary"></b-spinner>
          <div class="mt-2 text-muted">Checking fee schedule...</div>
        </div>
        <div v-else>
          <!-- Student's Degree Info -->
          <div class="alert alert-light border mb-3">
            <h6 class="mb-2">Student Degree Record</h6>
            <div class="row small">
              <div class="col-md-4"><strong>Programme Code:</strong> {{ feeCheck.prog_code || 'N/A' }}</div>
              <div class="col-md-3"><strong>Grant Type:</strong> {{ feeCheck.grant_type || 'N/A' }}</div>
              <div class="col-md-3"><strong>Admission Session:</strong> {{ feeCheck.admission_session || 'N/A' }}</div>
              <div class="col-md-2"><strong>Year Count:</strong> {{ feeCheck.year_count }}</div>
            </div>
          </div>

          <!-- Match Result -->
          <div v-if="feeCheck.items.length > 0" class="alert alert-success mb-3">
            <b-icon icon="check-circle-fill" class="mr-1"></b-icon>
            <strong>{{ feeCheck.items.length }}</strong> fee item(s) found for this combination
            — Total: <strong>{{ Number(feeCheck.totalAmount).toLocaleString() }}</strong>
          </div>
          <div v-else class="alert alert-danger mb-3">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <b-icon icon="x-circle-fill" class="mr-1"></b-icon>
                <strong>No fee schedule entries found</strong> matching prog_code <code>{{ feeCheck.prog_code }}</code>
                + grant_type <code>{{ feeCheck.grant_type }}</code> + status PUBLISHED.
                <br><small>Fix: Edit the student's degree to match an existing fee schedule, or create fee items for this programme code.</small>
              </div>
              <b-button size="sm" variant="primary" @click="editDegreeFromFeeCheck" class="ml-2 flex-shrink-0">
                <b-icon icon="pencil" class="mr-1"></b-icon> Edit Degree
              </b-button>
            </div>
          </div>

          <!-- Fee Items for current year -->
          <div v-if="feeCheck.items.length > 0">
            <h6>Fee items for Year {{ feeCheck.year_count }}:</h6>
            <b-table
              :items="feeCheck.currentYearItems"
              :fields="feeCheckFields"
              small
              bordered
              responsive
            >
              <template #cell(amount)="data">
                {{ Number(data.value).toLocaleString() }}
              </template>
            </b-table>
            <div v-if="feeCheck.currentYearItems.length === 0" class="alert alert-warning small">
              <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
              No items for <strong>Year {{ feeCheck.year_count }}</strong>. Fee schedule has items for other years only.
              <span v-if="feeCheck.availableYears.length"> Available years: {{ feeCheck.availableYears.join(', ') }}</span>
            </div>
          </div>

          <!-- Similar prog_codes hint -->
          <div v-if="feeCheck.items.length === 0 && feeCheck.similar.length > 0" class="mt-3">
            <h6>Similar programme codes in fee schedule:</h6>
            <div class="d-flex flex-wrap">
              <b-button
                v-for="s in feeCheck.similar"
                :key="s"
                size="sm"
                variant="outline-secondary"
                class="mr-2 mb-2"
                @click="applyProgCodeFromFeeCheck(s)"
                :title="'Click to set prog_code to ' + s"
              >
                {{ s }}
              </b-button>
            </div>
            <small class="text-muted">Click a code above to open Edit Degree with that prog_code pre-filled.</small>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      searchQuery: "",
      searching: false,
      searched: false,
      applicants: [],
      students: [],
      resending: null,
      requerying: null,
      creatingStudent: null,
      updatingContact: false,
      loadingLetter: false,
      admissionLetterHtml: "",
      loadingLogs: false,
      editContact: {
        applicant: null,
        email: "",
        phone: "",
      },
      auditLogs: [],
      auditPage: 1,
      auditPerPage: 25,
      auditFilter: {
        action: "",
        date_from: "",
        date_to: "",
      },
      applicationFields: [
        { key: "application_number", label: "App No.", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "created_at", label: "Date", sortable: true, formatter: (v) => v ? new Date(v).toLocaleDateString("en-GB") : "" },
        { key: "actions", label: "Actions", class: "text-center", thStyle: { width: "120px" } },
      ],
      programmes: [],
      editDegree: {
        studentId: null,
        degreeId: null,
        prog_id: null,
        degree: "",
        prog_code: "",
        grant_type: "",
        admission_session: "",
        status: "",
      },
      savingDegree: false,
      checkingFees: null,
      feeCheck: {
        loading: false,
        student: null,
        degree: null,
        prog_code: "",
        grant_type: "",
        admission_session: "",
        year_count: 1,
        items: [],
        currentYearItems: [],
        availableYears: [],
        similar: [],
        totalAmount: 0,
      },
      feeCheckFields: [
        { key: "item", label: "Item", sortable: true },
        { key: "amount", label: "Amount", sortable: true },
        { key: "year", label: "Year", sortable: true, class: "text-center" },
        { key: "grant_type", label: "Grant Type", sortable: true },
        { key: "session", label: "Session", sortable: true },
        { key: "status", label: "Status", sortable: true },
      ],
      degreeFields: [
        { key: "degree", label: "Degree", sortable: true },
        { key: "prog_code", label: "Programme Code", sortable: true },
        { key: "programme_name", label: "Programme", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "grant_type", label: "Grant Type", sortable: true },
        { key: "admission_session", label: "Session", sortable: true },
        { key: "degree_actions", label: "Actions", class: "text-center", thStyle: { width: "80px" } },
      ],
      applicantTxnFields: [
        { key: "details", label: "Type", sortable: true },
        { key: "amount", label: "Amount", sortable: true },
        { key: "reference", label: "RRR / Ref" },
        { key: "gateway", label: "Gateway", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "created_at", label: "Date", sortable: true, formatter: (v) => v ? new Date(v).toLocaleDateString("en-GB") : "" },
        { key: "txn_actions", label: "Actions", class: "text-center", thStyle: { width: "100px" } },
      ],
      studentTxnFields: [
        { key: "type", label: "Type", sortable: true },
        { key: "degree", label: "Degree", sortable: true },
        { key: "amount", label: "Amount", sortable: true },
        { key: "reference", label: "RRR / Ref" },
        { key: "gateway", label: "Gateway", sortable: true },
        { key: "session", label: "Session", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "created_at", label: "Date", sortable: true, formatter: (v) => v ? new Date(v).toLocaleDateString("en-GB") : "" },
        { key: "txn_actions", label: "Actions", class: "text-center", thStyle: { width: "100px" } },
      ],
      registrationFields: [
        { key: "session_name", label: "Session", sortable: true },
        { key: "semester_name", label: "Semester", sortable: true },
        { key: "level", label: "Level", sortable: true },
        { key: "reg_status", label: "Status", sortable: true },
        { key: "created_at", label: "Date", sortable: true, formatter: (v) => v ? new Date(v).toLocaleDateString("en-GB") : "" },
      ],
      semesterFields: [
        { key: "session_name", label: "Session", sortable: true },
        { key: "semester_name", label: "Semester", sortable: true },
      ],
      courseFields: [
        { key: "course_code", label: "Code", sortable: true },
        { key: "course_name", label: "Course", sortable: true },
        { key: "course_unit", label: "Units", sortable: true, class: "text-center" },
        { key: "course_status", label: "Type", sortable: true, class: "text-center" },
      ],
      idcardFields: [
        { key: "session", label: "Session", sortable: true },
        { key: "created_at", label: "Date", sortable: true, formatter: (v) => v ? new Date(v).toLocaleDateString("en-GB") : "" },
      ],
      auditFields: [
        { key: "admin_name", label: "Admin", sortable: true },
        { key: "action", label: "Action", sortable: true },
        { key: "target_type", label: "Target", sortable: true },
        { key: "details", label: "Details" },
        { key: "created_at", label: "Date/Time", sortable: true },
      ],
    };
  },
  methods: {
    searchHelpdesk() {
      if (!this.searchQuery.trim()) return;
      this.searching = true;
      this.searched = false;
      axios
        .get("admin/helpdesk/search", { params: { q: this.searchQuery.trim() } })
        .then((res) => {
          this.applicants = res.data.applicants || [];
          this.students = res.data.students || [];
          this.searched = true;
        })
        .catch(() => Toast.fire({ icon: "error", title: "Search failed" }))
        .finally(() => (this.searching = false));
    },

    createStudentRecord(applicant) {
      if (!confirm(`Create student record for ${applicant.email}?`)) return;
      this.creatingStudent = applicant.id;
      axios
        .post("admin/helpdesk/create-student", { applicant_id: applicant.id })
        .then((res) => {
          Toast.fire({ icon: "success", title: res.data.message || "Student record created" });
          applicant.has_student_record = true;
        })
        .catch((err) => {
          Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to create student record" });
        })
        .finally(() => (this.creatingStudent = null));
    },

    openEditContactModal(applicant) {
      this.editContact.applicant = applicant;
      this.editContact.email = applicant.email;
      this.editContact.phone = applicant.mobile || "";
      this.$bvModal.show("edit-contact-modal");
    },

    updateApplicantContact(evt) {
      evt.preventDefault();
      this.updatingContact = true;
      axios
        .put("admin/helpdesk/applicant/" + this.editContact.applicant.id + "/contact", {
          email: this.editContact.email,
          phone: this.editContact.phone,
        })
        .then(() => {
          Toast.fire({ icon: "success", title: "Contact updated" });
          this.$bvModal.hide("edit-contact-modal");
          this.searchHelpdesk();
        })
        .catch((err) => {
          let msg = err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : "Failed to update contact";
          Toast.fire({ icon: "error", title: msg });
        })
        .finally(() => (this.updatingContact = false));
    },

    resetApplicantPassword(applicant) {
      Swal.fire({
        title: "Reset Applicant Password?",
        text: "A new temporary password will be generated and emailed to " + applicant.email,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, reset",
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .post("admin/helpdesk/applicant/" + applicant.id + "/reset-password")
            .then((res) => {
              Swal.fire({
                title: "Password Reset",
                html: "New temporary password: <strong>" + res.data.temp_password + "</strong><br><br><small>This has also been emailed to the applicant.</small>",
                icon: "success",
              });
            })
            .catch(() => Toast.fire({ icon: "error", title: "Failed to reset password" }));
        }
      });
    },

    resetStudentPassword(student) {
      Swal.fire({
        title: "Reset Student Password?",
        text: "A new temporary password will be generated and emailed to " + student.email,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, reset",
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .post("admin/helpdesk/student/" + student.id + "/reset-password")
            .then((res) => {
              Swal.fire({
                title: "Password Reset",
                html: "New temporary password: <strong>" + res.data.temp_password + "</strong><br><br><small>This has also been emailed to the student.</small>",
                icon: "success",
              });
            })
            .catch(() => Toast.fire({ icon: "error", title: "Failed to reset password" }));
        }
      });
    },

    resendAdmissionEmail(application) {
      Swal.fire({
        title: "Resend Admission Email?",
        text: "The admission letter will be resent to the applicant's email.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, resend",
      }).then((result) => {
        if (result.isConfirmed) {
          this.resending = application.id;
          axios
            .post("admin/helpdesk/resend-admission-email/" + application.id)
            .then(() => Toast.fire({ icon: "success", title: "Admission email resent" }))
            .catch(() => Toast.fire({ icon: "error", title: "Failed to resend email" }))
            .finally(() => (this.resending = null));
        }
      });
    },

    viewAdmissionLetter(application) {
      this.admissionLetterHtml = "";
      this.loadingLetter = true;
      this.$bvModal.show("admission-letter-modal");
      axios
        .get("admin/helpdesk/admission-letter/" + application.id)
        .then((res) => {
          this.admissionLetterHtml = res.data.letter || "";
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load admission letter" }))
        .finally(() => (this.loadingLetter = false));
    },

    requeryRow(type, txn) {
      var ref = txn.rrr || txn.gateway_ref;
      if (!ref) return Toast.fire({ icon: "error", title: "No reference found for this transaction" });
      this.requerying = ref;
      axios
        .post("admin/helpdesk/reconcile-payment", { type: type, reference: ref })
        .then((res) => {
          var status = res.data.new_status || (res.data.data ? res.data.data.status : "");
          var icon = status === "SUCCESS" ? "success" : status === "PENDING" ? "warning" : "error";
          var amount = res.data.data ? Number(res.data.data.amount).toLocaleString() : "N/A";
          Swal.fire({
            title: "Gateway Response",
            html: "<strong>Status:</strong> " + status +
              "<br><strong>Amount:</strong> " + amount +
              "<br><br>" + (res.data.message || ""),
            icon: icon,
          });
          if (this.searched) this.searchHelpdesk();
        })
        .catch((err) => {
          var msg = err.response && err.response.data && err.response.data.message
            ? err.response.data.message
            : "Failed to requery payment";
          Toast.fire({ icon: "error", title: msg });
        })
        .finally(() => (this.requerying = null));
    },

    loadAuditLogs() {
      this.loadingLogs = true;
      let params = {};
      if (this.auditFilter.action) params.action = this.auditFilter.action;
      if (this.auditFilter.date_from) params.date_from = this.auditFilter.date_from;
      if (this.auditFilter.date_to) params.date_to = this.auditFilter.date_to;
      axios
        .get("admin/audit-logs", { params: params })
        .then((res) => {
          var result = res.data.data || {};
          this.auditLogs = result.data || result || [];
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load audit logs" }))
        .finally(() => (this.loadingLogs = false));
    },

    statusVariant(status) {
      var map = {
        approved: "success",
        denied: "danger",
        "recommended for admission": "info",
        "submitted under processing": "warning",
        "awaiting submission": "secondary",
        pending: "warning",
      };
      return map[status] || "secondary";
    },

    formatAction(action) {
      return (action || "").replace(/_/g, " ").replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    },

    formatDetails(details) {
      if (!details) return "";
      if (typeof details === "string") {
        try { details = JSON.parse(details); } catch (e) { return details; }
      }
      return Object.entries(details)
        .map(function (entry) { return entry[0] + ": " + entry[1]; })
        .join(", ");
    },

    formatDateTime(dateStr) {
      if (!dateStr) return "";
      var d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
        " " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    },

    loadProgrammes() {
      axios
        .get("admin/programmes")
        .then((res) => {
          this.programmes = res.data.data || [];
        })
        .catch(() => {});
    },

    filterProgrammes(options, search) {
      var s = search.toLowerCase();
      return options.filter(function (o) {
        return (
          (o.programme && o.programme.toLowerCase().indexOf(s) > -1) ||
          (o.prog_code && o.prog_code.toLowerCase().indexOf(s) > -1) ||
          (o.degree && o.degree.toLowerCase().indexOf(s) > -1)
        );
      });
    },

    openEditDegreeModal(student, degree) {
      this.editDegree.studentId = student.id;
      this.editDegree.degreeId = degree.id;
      this.editDegree.prog_id = degree.prog_id || null;
      this.editDegree.degree = degree.degree || "";
      this.editDegree.prog_code = degree.prog_code || "";
      this.editDegree.grant_type = degree.grant_type || "";
      this.editDegree.admission_session = degree.admission_session || "";
      this.editDegree.status = degree.status || "CURRENT";
      if (!this.programmes.length) this.loadProgrammes();
      this.$bvModal.show("edit-degree-modal");
    },

    onProgrammeSelect(progId) {
      if (!progId) return;
      var prog = this.programmes.find(function (p) { return p.id === progId; });
      if (prog) {
        this.editDegree.degree = prog.degree || "";
        this.editDegree.prog_code = prog.prog_code || "";
      }
    },

    editDegreeFromFeeCheck() {
      if (!this.feeCheck.student || !this.feeCheck.degree) return;
      this.$bvModal.hide("fee-check-modal");
      this.openEditDegreeModal(this.feeCheck.student, this.feeCheck.degree);
    },

    applyProgCodeFromFeeCheck(progCode) {
      if (!this.feeCheck.student || !this.feeCheck.degree) return;
      this.$bvModal.hide("fee-check-modal");
      this.openEditDegreeModal(this.feeCheck.student, this.feeCheck.degree);
      this.$nextTick(() => {
        this.editDegree.prog_code = progCode;
        var parts = progCode.split("*");
        if (parts.length >= 2) {
          this.editDegree.degree = parts[1];
        }
      });
    },

    checkDegreeFees(student, degree) {
      this.checkingFees = degree.id;
      this.feeCheck.loading = true;
      this.feeCheck.student = student;
      this.feeCheck.degree = degree;
      this.feeCheck.prog_code = degree.prog_code || "";
      this.feeCheck.grant_type = degree.grant_type || "";
      this.feeCheck.admission_session = degree.admission_session || "";
      this.feeCheck.items = [];
      this.feeCheck.currentYearItems = [];
      this.feeCheck.availableYears = [];
      this.feeCheck.similar = [];
      this.feeCheck.totalAmount = 0;

      var semesters = student.semesters || [];
      var sessions = {};
      for (var i = 0; i < semesters.length; i++) {
        if (semesters[i].session_name) sessions[semesters[i].session_name] = true;
      }
      this.feeCheck.year_count = Math.max(Object.keys(sessions).length, 1);

      this.$bvModal.show("fee-check-modal");

      var progCode = degree.prog_code || "";
      var prefix = progCode.split("*")[0];

      axios
        .get("admin/fee-schedules", { params: { prog_code: progCode, status: "PUBLISHED" } })
        .then((res) => {
          var items = (res.data.data || []).filter((item) => item.grant_type === degree.grant_type);
          this.feeCheck.items = items;
          this.feeCheck.totalAmount = items.reduce((sum, item) => sum + (item.amount || 0), 0);
          this.feeCheck.currentYearItems = items.filter((item) => item.year == this.feeCheck.year_count);
          var years = {};
          items.forEach(function (item) { years[item.year] = true; });
          this.feeCheck.availableYears = Object.keys(years).sort();

          if (items.length === 0 && prefix) {
            axios
              .get("admin/fee-schedules", { params: { status: "PUBLISHED" } })
              .then((res2) => {
                var allItems = res2.data.data || [];
                var seen = {};
                var similar = [];
                for (var j = 0; j < allItems.length; j++) {
                  var pc = allItems[j].prog_code;
                  if (pc && !seen[pc] && pc.indexOf(prefix) === 0 && pc !== progCode) {
                    seen[pc] = true;
                    similar.push(pc);
                  }
                }
                this.feeCheck.similar = similar.sort();
              })
              .catch(() => {});
          }
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to check fee schedule" }))
        .finally(() => {
          this.feeCheck.loading = false;
          this.checkingFees = null;
        });
    },

    saveStudentDegree(evt) {
      evt.preventDefault();
      if (!this.editDegree.prog_id) {
        return Toast.fire({ icon: "error", title: "Please select a programme" });
      }
      this.savingDegree = true;
      axios
        .put(
          "admin/students/" + this.editDegree.studentId + "/degrees/" + this.editDegree.degreeId,
          {
            prog_id: this.editDegree.prog_id,
            degree: this.editDegree.degree,
            prog_code: this.editDegree.prog_code,
            grant_type: this.editDegree.grant_type,
            admission_session: this.editDegree.admission_session,
            status: this.editDegree.status,
          }
        )
        .then(() => {
          Toast.fire({ icon: "success", title: "Degree updated successfully" });
          this.$bvModal.hide("edit-degree-modal");
          this.searchHelpdesk();
        })
        .catch((err) => {
          var msg =
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : "Failed to update degree";
          Toast.fire({ icon: "error", title: msg });
        })
        .finally(() => (this.savingDegree = false));
    },
  },

  created() {
    this.loadProgrammes();
  },
};
</script>
